import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Calendar, Package2, MapPin, Truck, Home } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { CarrierBadge } from "../components/app/CarrierBadge";
import { StatusTimeline } from "../components/app/StatusTimeline";

const STEPS = [
    { code: "RECOLHIDO", label: "Recolhido", Icon: Package2 },
    { code: "EM_TRANSITO", label: "Em Trânsito", Icon: Truck },
    { code: "CENTRO_DISTRIBUICAO", label: "Distribuição", Icon: MapPin },
    { code: "EM_DISTRIBUICAO", label: "Em Entrega", Icon: Truck },
    { code: "ENTREGUE", label: "Entregue", Icon: Home },
];

function indexOfStatus(code) {
    const STEP_INDEX = {
        RECOLHIDO: 0,
        EM_TRANSITO: 1,
        CENTRO_DISTRIBUICAO: 2,
        EM_DISTRIBUICAO: 3,
        DISPONIVEL_LEVANTAMENTO: 3,
        ENTREGA_FALHADA: 3,
        EXCECAO: 1,
        DEVOLVIDO: 1,
        ENTREGUE: 4,
    };
    return STEP_INDEX[code] ?? 0;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function fmtEtaRange(eta) {
    if (!eta || !eta.min || !eta.max) return "—";
    try {
        const a = new Date(eta.min);
        const b = new Date(eta.max);
        const sameDay = a.toDateString() === b.toDateString();
        if (sameDay) return fmtDate(eta.min);
        const sameMonth =
            a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
        const aFmt = a.toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: sameMonth ? undefined : "short",
        });
        const bFmt = b.toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
        return `${aFmt} – ${bFmt}`;
    } catch {
        return "—";
    }
}

export default function TrackingDetail() {
    const { code } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    async function load(silent = false) {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/tracking/${encodeURIComponent(code)}`);
            setData(res.data);
        } catch (e) {
            if (!silent) {
                setError(formatApiError(e));
                setData(null);
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }

    useEffect(() => {
        load();
    }, [code]);

    // Auto-refresh every 60s so the customer always sees the latest carrier status
    useEffect(() => {
        const id = setInterval(() => load(true), 60000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
                        <ArrowLeft className="w-4 h-4" /> Voltar
                    </Link>
                    <Link to="/" className="flex items-center gap-2">
                        <div className="relative">
                            <div className="w-6 h-6 bg-[var(--tp-blue)]" />
                            <div className="absolute -right-1 -bottom-1 w-2.5 h-2.5 bg-[var(--tp-orange)]" />
                        </div>
                        <span className="font-black tracking-tight">
                            TRACKING<span className="text-[var(--tp-orange)]">PLUS</span>
                        </span>
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
                {loading && (
                    <div data-testid="tracking-loading" className="border border-slate-200 bg-white p-12 text-center text-slate-500">
                        A carregar...
                    </div>
                )}

                {!loading && error && (
                    <div className="border border-red-200 bg-red-50 p-10 text-center">
                        <h2 className="text-2xl font-black tracking-tight text-red-700">Envio não encontrado</h2>
                        <p className="mt-2 text-red-600">{error}</p>
                        <button
                            onClick={() => navigate("/")}
                            data-testid="back-home-button"
                            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-bold uppercase tracking-wider hover:bg-slate-800 transition"
                        >
                            Voltar à página inicial
                        </button>
                    </div>
                )}

                {!loading && data && (
                    <div className="space-y-8" data-testid="tracking-detail">
                        {/* Header card */}
                        <section className="bg-white border border-slate-200 p-6 lg:p-10">
                            <div className="flex flex-wrap items-start justify-between gap-6">
                                <div>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                                            Transportadora
                                        </span>
                                        <CarrierBadge code={data.carrier} />
                                    </div>
                                    <div className="mt-2 font-mono text-2xl lg:text-3xl font-bold text-slate-900" data-testid="tracking-code">
                                        {data.order_number || data.code}
                                    </div>
                                    {(data.recipient_name || data.recipient_locality) && (
                                        <div className="mt-2 text-sm text-slate-700 font-semibold" data-testid="recipient-info">
                                            {data.recipient_name}
                                            {data.recipient_name && data.recipient_locality ? " · " : ""}
                                            {data.recipient_locality && (
                                                <span className="text-slate-500 font-medium">{data.recipient_locality}</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-1 text-xs text-slate-400">
                                        Ref. transportadora:{" "}
                                        <span className="font-mono">{data.tracking_number}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setRefreshing(true);
                                        load();
                                    }}
                                    disabled={refreshing}
                                    data-testid="refresh-tracking-button"
                                    className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-sm font-bold uppercase tracking-wider hover:bg-slate-50 transition disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                                    Atualizar
                                </button>
                            </div>

                            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                                <div className="bg-white p-5">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                                        Estado atual
                                    </div>
                                    <div className="mt-1 text-xl font-black tracking-tight text-[var(--tp-orange)]" data-testid="current-status">
                                        {data.status_label}
                                    </div>
                                </div>
                                <div className="bg-white p-5">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
                                        <Calendar className="w-3.5 h-3.5" /> Entrega prevista
                                    </div>
                                    <div className="mt-1 text-xl font-black tracking-tight" data-testid="estimated-delivery">
                                        {fmtEtaRange(data.estimated_delivery)}
                                    </div>
                                </div>
                                <div className="bg-white p-5">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                                        Origem
                                    </div>
                                    <div className="mt-1 text-xl font-black tracking-tight">
                                        LOGISTICA PLUS
                                    </div>
                                </div>
                            </div>

                            {/* Stepper */}
                            <div className="mt-10">
                                <Stepper currentIdx={indexOfStatus(data.status_code)} />
                            </div>
                        </section>

                        {/* Timeline */}
                        <section className="bg-white border border-slate-200 p-6 lg:p-10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                                        Histórico
                                    </span>
                                    <h2 className="mt-1 text-2xl font-black tracking-tight">
                                        Eventos do envio
                                    </h2>
                                </div>
                            </div>
                            <StatusTimeline events={data.events} />
                        </section>
                    </div>
                )}
            </main>
        </div>
    );
}

function Stepper({ currentIdx }) {
    return (
        <ol className="grid grid-cols-5 gap-2" data-testid="status-stepper">
            {STEPS.map((s, i) => {
                const done = i < currentIdx;
                const active = i === currentIdx;
                const { Icon } = s;
                return (
                    <li key={s.code} className="flex flex-col items-center text-center">
                        <div
                            className={`w-10 h-10 flex items-center justify-center border ${
                                active
                                    ? "bg-[var(--tp-orange)] text-white border-[var(--tp-orange)] tp-pulse"
                                    : done
                                      ? "bg-[var(--tp-blue)] text-white border-[var(--tp-blue)]"
                                      : "bg-white text-slate-400 border-slate-300"
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                        </div>
                        <div className="mt-2 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-700">
                            {s.label}
                        </div>
                        {i < 4 && (
                            <div
                                className={`hidden md:block h-px w-full mt-[-22px] ${done ? "bg-[var(--tp-blue)]" : "bg-slate-200"}`}
                                style={{ transform: "translateX(50%)" }}
                            />
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
